import axios from 'axios'
import React from 'react'
import { HashLink } from 'react-router-hash-link'

import { ANCHOR_IDS } from '@constants/anchorIds'
import {
  Banner,
  Button,
  ConfirmModal,
  Icon,
  Toasts,
  Tooltip,
  useModalContext,
} from '@fourthwall/components'
import { RefreshIcon, TickIcon } from '@fourthwall/icons'
import { routing } from '@utils/routing'

import * as S from './MailchimpAudienceConnected.styled'
import { blockSyncMessage } from './MailchimpStoreConnected'

const MAILCHIMP_MODAL_THRESHOLD = 500
const MAILCHIMP_BLOCK_SYNC_THRESHOLD = 10000

type Props = {
  listId: string
  createStore: (listId: string | undefined) => void
  isCreatingStore: boolean
  hasSynced: boolean
  recentSync: boolean
  createSyncJob: () => Promise<void>
  isCreatingSyncJob: boolean
  abandonedCheckoutIsOff: boolean
  dataCount: number
}

export const MailchimpAudienceConnected = ({
  listId,
  createStore,
  isCreatingStore,
  createSyncJob,
  isCreatingSyncJob,
  hasSynced,
  abandonedCheckoutIsOff,
  dataCount,
}: Props) => {
  const { open } = useModalContext()

  const handleCreateStore = async (listId: string) => {
    try {
      await createStore(listId)
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        Toasts.notify(
          'Access denied. This is likely because you have too many lists or stores in your Mailchimp account. Please try going through and deleting some.',
          { type: 'error' },
        )
      } else {
        Toasts.notify('Failed to refresh Mailchimp lists. Please try again.', { type: 'error' })
        console.error('Failed to create Mailchimp store:', error)
      }
    }
  }

  const handleSyncClick = () => {
    if (dataCount > MAILCHIMP_MODAL_THRESHOLD) {
      open(ConfirmModal, {
        title: 'Sync customer data with Mailchimp?',
        text: 'This will sync your Fourthwall customer data to Mailchimp. This action may take a few minutes to complete and can increase your monthly Mailchimp charges.',
        confirmLabel: 'Sync Now',
        confirmAppearance: 'primary',
        onConfirm: createSyncJob,
      })
    } else {
      createSyncJob()
    }
  }

  const blockSync = dataCount > MAILCHIMP_BLOCK_SYNC_THRESHOLD

  return (
    <S.Container>
      <S.Title>
        <Icon component={TickIcon} width={24} height={24} />
        <div>Successfully Connected to Mailchimp</div>
      </S.Title>

      <S.Description>
        <p>
          Your Fourthwall audience is now connected to Mailchimp without e-commerce features. You
          can now manage your email subscribers and create engaging email campaigns to connect with
          your community.
        </p>
        <p>
          Ecommerce features such as Promotions, Products, and Orders are not available. Once you do
          upgrade to a Standard account, you can use the button below to access these features.
        </p>
      </S.Description>

      <S.ButtonContainer>
        <Button
          label="Reconnect with E-commerce features"
          appearance="secondary"
          onClick={() => handleCreateStore(listId)}
          loading={isCreatingStore}
          disabled={isCreatingStore}
        />
      </S.ButtonContainer>

      <S.SyncSection>
        <Tooltip
          reference={
            <Button
              label="Sync Now"
              appearance="primary"
              size="medium"
              leftIcon={<Icon component={RefreshIcon} width={16} height={16} />}
              onClick={handleSyncClick}
              loading={isCreatingSyncJob}
              disabled={isCreatingSyncJob || hasSynced || blockSync}
            />
          }
        >
          {blockSyncMessage(hasSynced, blockSync)}
        </Tooltip>
      </S.SyncSection>

      {!abandonedCheckoutIsOff && (
        <Banner appearance="warning">
          Fourthwall's default abandoned cart emails are turned on. If you're using Mailchimp to
          send abandoned cart emails make sure to{' '}
          <HashLink
            to={{
              pathname: routing.settings.checkout,
              hash: ANCHOR_IDS.ABANDONED_CHECKOUT_REMINDERS,
            }}
          >
            disable Fourthwall cart abandonment emails
          </HashLink>
          .
        </Banner>
      )}

      {/* <S.Footer>
        Need help getting started? Visit our{' '}
        <ButtonClean
          label="Help Center"
          href={routing.external.helpCenter.mailchimp}
          target="_blank"
        />{' '}
        for detailed guides and best practices.
      </S.Footer> */}
    </S.Container>
  )
}
